import { useEffect, useRef, useState } from "react";
import type { BadgeView } from "./view-model";
import { PixelIcon } from "./PixelIcon";

export interface TrophyCabinetProps {
  badges?: BadgeView[];
  dripWeeksLeft?: number | null;
  onClose(): void;
  initialShelf?: number;
}

export type TrophyTier = "gold" | "silver" | "bronze";

export function trophyTier(id: string): TrophyTier {
  switch (id) {
    case "full-score":
    case "near-perfect":
    case "twice-great":
    case "ten-weeks":
      return "gold";
    case "six-weeks":
    case "great-week":
    case "good-week":
    case "dressed-tank":
      return "silver";
    default:
      return "bronze";
  }
}

/** Detect horizontal swipe over 40px and lock when vertical scroll is greater. */
export function resolveShelfSwipe(
  start: { x: number; y: number },
  end: { x: number; y: number },
): "prev" | "next" | null {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX < 0 ? "next" : "prev";
  }
  return null;
}

function dripNote(dripWeeksLeft: number | null | undefined): string {
  if (dripWeeksLeft === null || dripWeeksLeft === undefined) {
    return "Hatch your first pet to start. Items from badges can be earned during your first 12 weeks.";
  }
  if (dripWeeksLeft === 0) {
    return "The first 12 weeks are over: badges are just badges now and the items are in the shop.";
  }
  const weekStr = dripWeeksLeft === 1 ? "1 week" : `${dripWeeksLeft} weeks`;
  return `${weekStr} left to earn items from badges. After that badges are just badges and the items are in the shop.`;
}

function TrophySvg({ id, earned }: { id: string; earned: boolean }) {
  const tier = trophyTier(id);
  const gradId = `trophy-${tier}`;
  if (!earned) {
    return (
      <svg viewBox="0 0 64 64" width="64" height="64" role="img" aria-hidden="true" className="cab-silhouette">
        <ellipse cx="32" cy="60" rx="16" ry="3" fill="rgba(0,0,0,0.5)" />
        <path d="M16 16 C10 16 10 30 18 32 M48 16 C54 16 54 30 46 32" fill="none" stroke="#28332d" strokeWidth="3" strokeLinecap="round" />
        <path d="M18 12 H46 V26 C46 36 38 42 32 42 C26 42 18 36 18 26 Z" fill="#1c2420" stroke="#28332d" strokeWidth="1.5" />
        <path d="M29 42 H35 V52 H44 V56 H20 V52 H29 Z" fill="#1c2420" stroke="#28332d" strokeWidth="1.5" />
        <line x1="32" y1="34" x2="39" y2="44" stroke="#a67828" strokeWidth="1.5" strokeDasharray="2,2" />
        <rect x="34" y="42" width="14" height="13" rx="2" fill="#c8963e" stroke="#7d5718" strokeWidth="1" />
        <text x="41" y="52" fill="#24140b" fontSize="10" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">?</text>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" role="img" aria-hidden="true" className={`trophy-svg ${gradId}`}>
      <defs>
        <linearGradient id="trophy-gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffdf79" />
          <stop offset="30%" stopColor="#f7c948" />
          <stop offset="65%" stopColor="#e2a319" />
          <stop offset="85%" stopColor="#b7791f" />
          <stop offset="100%" stopColor="#8c5810" />
        </linearGradient>
        <linearGradient id="trophy-silver" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="30%" stopColor="#e2e8f0" />
          <stop offset="65%" stopColor="#cbd5e1" />
          <stop offset="85%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#64748b" />
        </linearGradient>
        <linearGradient id="trophy-bronze" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#df944b" />
          <stop offset="30%" stopColor="#cd7f32" />
          <stop offset="65%" stopColor="#b86d26" />
          <stop offset="85%" stopColor="#9c571a" />
          <stop offset="100%" stopColor="#7c4210" />
        </linearGradient>
      </defs>
      <ellipse cx="32" cy="60" rx="18" ry="3.5" fill="rgba(0,0,0,0.4)" />
      <path d="M15 16 C7 16 7 30 18 32 M49 16 C57 16 57 30 46 32" fill="none" stroke={`url(#${gradId})`} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M18 12 H46 V26 C46 36 38 42 32 42 C26 42 18 36 18 26 Z" fill={`url(#${gradId})`} stroke="#5a3e10" strokeWidth="1" />
      <ellipse cx="32" cy="12" rx="14" ry="2.5" fill={`url(#${gradId})`} stroke="#5a3e10" strokeWidth="1" />
      <path d="M29 42 H35 V50 H44 V56 H20 V50 H29 Z" fill={`url(#${gradId})`} stroke="#5a3e10" strokeWidth="1" />
      <path d="M21 16 C21 26 25 34 29 38" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
      <polygon points="32,20 34,25 39,25 35,28 37,33 32,30 27,33 29,28 25,25 30,25" fill="#ffffff" opacity="0.85" />
    </svg>
  );
}

export function TrophyCabinet({
  badges,
  dripWeeksLeft,
  onClose,
  initialShelf = 0,
}: TrophyCabinetProps) {
  const [shelfIndex, setShelfIndex] = useState(initialShelf);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const detailBackRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const lastOpenedRef = useRef<string | null>(null);
  // Opening a trophy removes the shelf button that was pressed: move focus to the details, and back to that trophy when they close.
  useEffect(() => {
    if (selectedId) {
      lastOpenedRef.current = selectedId;
      detailBackRef.current?.focus();
    } else if (lastOpenedRef.current) {
      const id = lastOpenedRef.current;
      lastOpenedRef.current = null;
      rootRef.current?.querySelector<HTMLElement>(`[data-testid=badge-card][data-badge-id="${id}"]`)?.focus();
    }
  }, [selectedId]);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Group badges into shelves of 3
  const shelves: BadgeView[][] = [];
  if (badges) {
    for (let i = 0; i < badges.length; i += 3) {
      shelves.push(badges.slice(i, i + 3));
    }
  }

  const totalShelves = shelves.length;
  const safeShelfIndex = totalShelves === 0 ? 0 : Math.max(0, Math.min(shelfIndex, totalShelves - 1));

  const goPrev = () => {
    setShelfIndex((prev) => Math.max(0, prev - 1));
  };

  const goNext = () => {
    setShelfIndex((prev) => Math.min(totalShelves - 1, prev + 1));
  };

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (selectedId) {
          setSelectedId(null);
        } else {
          onClose();
        }
        return;
      }
      if (selectedId) return;
      if (totalShelves <= 1) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        setShelfIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        setShelfIndex((prev) => Math.min(totalShelves - 1, prev + 1));
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, selectedId, totalShelves]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) {
      touchStartRef.current = { x: t.clientX, y: t.clientY };
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    if (!touchStartRef.current || !t) return;
    const end = { x: t.clientX, y: t.clientY };
    const swipe = resolveShelfSwipe(touchStartRef.current, end);
    touchStartRef.current = null;
    if (swipe === "next") goNext();
    else if (swipe === "prev") goPrev();
  };

  const earnedCount = badges ? badges.filter((b) => b.earned).length : 0;
  const selectedBadge = badges?.find((b) => b.id === selectedId) ?? null;

  return (
    <section
      ref={rootRef}
      className="trophy-cabinet cab-wood"
      role="dialog"
      aria-modal="true"
      aria-label="Trophies"
      data-testid="trophy-case"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Decorative brass hardware on frame */}
      <div className="cab-hinge cab-hinge-top" aria-hidden="true" />
      <div className="cab-hinge cab-hinge-bottom" aria-hidden="true" />
      <div className="cab-latch" aria-hidden="true" />

      <div className="cab-head">
        <h2 id="cabh">{selectedBadge ? "Trophy detail" : "Trophies"}</h2>
        <button
          ref={closeRef}
          className="btn cab-close-btn"
          type="button"
          data-testid="sheet-close"
          onClick={onClose}
        >
          Back to the game
        </button>
      </div>

      <div className="cab-interior">
        <div className="cab-light" aria-hidden="true" />
        <div className="cab-glass" aria-hidden="true" />

        <div className="cab-felt">
          {!badges ? (
            <div className="note" data-testid="badges-unavailable">
              Trophies are not available right now.
            </div>
          ) : selectedBadge ? (
            <div className="trophy-detail cab-detail-card" data-testid="trophy-detail">
              <div className="cab-detail-head">
                <h4>{selectedBadge.name}</h4>
                <span
                  data-testid="badge-status"
                  className={`cab-badge-status ${selectedBadge.earned ? "earned" : "locked"}`}
                >
                  {selectedBadge.earned ? (
                    <>
                      <PixelIcon name="check" /> Earned
                    </>
                  ) : (
                    "Locked"
                  )}
                </span>
              </div>

              <div className="cab-detail-text">
                <b>What it takes: </b>
                {selectedBadge.text}
              </div>

              <div className="cab-detail-hint">
                <b>{selectedBadge.earned ? "Earned: " : "How to earn: "}</b>
                {selectedBadge.earned
                  ? selectedBadge.earnedAt ?? "Earned"
                  : selectedBadge.hint ?? selectedBadge.text}
              </div>

              {selectedBadge.rewardText && selectedBadge.rewardText !== "Badge" ? (
                <div className="cab-detail-reward">
                  <b>Reward: </b>
                  <span className="reward-tag">{selectedBadge.rewardText}</span>{" "}
                  <small>
                    {selectedBadge.rewardPlaced
                      ? "(Placed in tank)"
                      : selectedBadge.granted
                        ? "(In your collection)"
                        : "(Item in shop)"}
                  </small>
                </div>
              ) : null}

              <button
                ref={detailBackRef}
                className="btn alt"
                type="button"
                data-testid="trophy-detail-back"
                onClick={() => setSelectedId(null)}
              >
                <PixelIcon name="back" /> Back to shelf
              </button>
            </div>
          ) : (
            <div className="cab-tracks shelves" data-testid="badges-list">
              {shelves.map((shelf, idx) => (
                <div
                  key={idx}
                  className={`cabinet-shelf ${idx === safeShelfIndex ? "cabinet-shelf-current" : ""}`}
                  data-shelf={idx}
                  hidden={idx !== safeShelfIndex}
                  aria-hidden={idx !== safeShelfIndex ? "true" : undefined}
                >
                  <div className="cab-row">
                    {shelf.map((badge) => (
                      <button
                        key={badge.id}
                        type="button"
                        className="trophy-btn"
                        data-testid="badge-card"
                        data-badge-id={badge.id}
                        data-earned={badge.earned ? "true" : "false"}
                        onClick={() => setSelectedId(badge.id)}
                        aria-label={`${badge.name}: ${badge.earned ? "Earned" : "Locked"}`}
                      >
                        <div className="trophy-stage">
                          <TrophySvg id={badge.id} earned={badge.earned} />
                        </div>
                        <div className="cab-plaque">
                          <span className="cab-plaque-text">{badge.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="cab-shelf-plank" aria-hidden="true" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!selectedBadge && badges && totalShelves > 0 ? (
        <div className="cab-nav">
          <button
            type="button"
            className="cab-arrow cab-prev"
            aria-label="Previous shelf"
            disabled={safeShelfIndex === 0}
            onClick={goPrev}
          >
            <PixelIcon name="prev" size={24} />
          </button>

          <div className="cab-nav-center">
            <span className="cab-counter">{`Shelf ${safeShelfIndex + 1} of ${totalShelves}`}</span>
            <div className="cab-dots" role="tablist" aria-label="Trophy shelves">
              {shelves.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`cab-dot ${i === safeShelfIndex ? "active" : ""}`}
                  aria-label={`Go to shelf ${i + 1}`}
                  aria-current={i === safeShelfIndex ? "true" : undefined}
                  onClick={() => setShelfIndex(i)}
                />
              ))}
            </div>
          </div>

          <button
            type="button"
            className="cab-arrow cab-next"
            aria-label="Next shelf"
            disabled={safeShelfIndex >= totalShelves - 1}
            onClick={goNext}
          >
            <PixelIcon name="next" size={24} />
          </button>
        </div>
      ) : null}

      <div className="trophy-drip cab-drip-card note" data-testid="badges-drip">
        <div className="cab-drip-count" data-testid="trophy-count">
          <span>{`${earnedCount} of ${badges?.length ?? 12} earned`}</span>
        </div>
        <p>{dripNote(dripWeeksLeft)}</p>
      </div>
    </section>
  );
}

export const BadgesSheet = TrophyCabinet;
export const TrophyCase = TrophyCabinet;
export type BadgesSheetProps = TrophyCabinetProps;

